import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import Spinner from '@components/Spinner';
import useEventTracking from '@hooks/useEventTracking';
import useRecommendedNewsletters from '@hooks/useRecommendedNewsletters';
import { TrackingLocation, impression } from '@tracking';

import NewsletterCards from './NewsletterCards';
import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS } from './constants';
import useRegisterSection from './useRegisterSection';

const ForYouNewsletters = () => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.FeaturedNewsletters,
  });
  const { newsletters, isLoadingNewsletters } = useRecommendedNewsletters();

  const newsletterDisplayText = useMemo(() => {
    if (!newsletters) return FOR_YOU_ENGAGEMENT_LABELS.NotSubscribed.Newsletters;

    const currentNumberSubscribedTo =
      newsletters?.filter((newsletter) => newsletter.currentUserSubscribed)?.length ?? 0;
    return newsletters.length - currentNumberSubscribedTo < Math.ceil(newsletters.length / 2)
      ? FOR_YOU_ENGAGEMENT_LABELS.Subscribed.Newsletters
      : FOR_YOU_ENGAGEMENT_LABELS.NotSubscribed.Newsletters;
  }, [newsletters]);

  useRegisterSection(
    FOR_YOU_SECTION_IDS.Newsletters,
    isLoadingNewsletters || !!newsletters?.length
  );

  if (!isLoadingNewsletters && !newsletters?.length) return null;

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.Newsletters}
      highlight={newsletterDisplayText.label}
      subtitle={newsletterDisplayText.subtitle}
      title={newsletterDisplayText.title}
    >
      <Track>
        <InView
          onChange={(inView) => {
            inView &&
              trackEvent(impression({ impressionElement: FOR_YOU_SECTION_IDS.Newsletters }));
          }}
          triggerOnce
        >
          {!isLoadingNewsletters && newsletters ? (
            <div className="-mx-16 mt-32 flex flex-row flex-nowrap justify-start gap-20 overflow-x-auto px-16 sm:mx-0 sm:px-0 md:grid md:grid-cols-4">
              <NewsletterCards newsletters={newsletters} />
            </div>
          ) : (
            <div className="grid w-full place-items-center">
              <Spinner marginY="small" />
            </div>
          )}
        </InView>
      </Track>
    </ForYouSection>
  );
};

export default ForYouNewsletters;
